import dynamic from "next/dynamic";
import { useState, useRef, useContext, useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useQuery } from "@apollo/client/react/hooks";
import { CartContext } from "lib/cartContext";
import { ProductContext } from "lib/productContext";
import Container from "components/shared/container/Container";
import Packaging from "components/product/configurator/packaging/Packaging";
import QuantityValue from "./quantityValue/QuantityValue";
import PersonalMessage from "./personalMessage/PersonalMessage";
import GiftWrap from "./giftwrap/GiftWrap";
import AddPackage from "./addPackage/AddPackage";
import Cart from "./cart/Cart";
import SummaryBar from "../summaryBar/SummaryBar";

import { GetProductOptions } from "../../../helpers/api/cadeauConcepten/queries/products.gql";

import {
    ConfiguratorStyled,
    Form,
    Title,
    Placeholder,
    Switch
} from "./Configurator.styled";
import Upsell from "./upsell/Upsell";

const Overlay = dynamic(() => import("components/shared/throbber/Overlay"));

function Configurator() {
    let personalMessage;
    let giftWrap;

    const configRef = useRef();
    const quantityValueRef = useRef();
    const personalMessageRef = useRef();
    const giftWrapRef = useRef();
    const upsellRef = useRef();
    const packagingRef = useRef();
    const cartRef = useRef();

    const methods = useForm({
        defaultValues: {
            quantity: 1,
            value: 10
        }
    });
    const [active, setActive] = useState(undefined);
    const { showOverlay, cartData, cartLoading } = useContext(CartContext);
    const {
        productData,
        setProductData,
        productLoading,
        setProductLoading,
        productError,
        setProductError
    } = useContext(ProductContext);

    const {
        data: optionsData,
        loading: optionsLoading,
        error: optionsError
    } = useQuery(GetProductOptions, {
        notifyOnNetworkStatusChange: true,
        variables: {
            slug: "lees-Cadeau"
        }
    });

    useEffect(() => {
        setProductLoading(optionsLoading);
        if (optionsError) {
            setProductError(optionsError);
        }
        if (!optionsLoading && optionsData && !productData) {
            setProductData(optionsData);
        }
    }, [
        optionsData,
        optionsLoading,
        optionsError,
        productData,
        setProductData,
        setProductLoading,
        setProductError
    ]);

    if (!productLoading && !productError) {
        personalMessage = productData?.product?.personalMessage;
        giftWrap = productData?.product?.giftwrap;
    }

    function setActivePackage(id) {
        setActive(id);
    }

    const packageNumber = !cartLoading
        ? cartData?.cart?.items?.length + 1 ?? 0
        : 0;

    return (
        <ConfiguratorStyled>
            <Container>
                <FormProvider {...methods}>
                    <Cart
                        active={active}
                        handleActive={setActivePackage}
                        ref={cartRef}
                    />
                    <Switch show={active === undefined}>
                        <Form ref={configRef}>
                            <Title>
                                Lees! Cadeaukaart
                                {packageNumber > 1 &&
                                    ` pakket ${packageNumber}`}{" "}
                                samenstellen
                            </Title>
                            <QuantityValue ref={quantityValueRef} />
                            <PersonalMessage
                                price={personalMessage?.price}
                                ref={personalMessageRef}
                            />
                            <Packaging packagingRef={packagingRef} />
                            <GiftWrap
                                price={giftWrap?.price}
                                ref={giftWrapRef}
                            />
                            <Upsell upsellRef={upsellRef} />
                            {showOverlay && <Overlay />}
                        </Form>
                    </Switch>
                    <Switch show={active !== undefined}>
                        <Placeholder
                            onClick={() => setActivePackage(undefined)}
                        >
                            <span>Nieuwe pakket configureren</span>
                        </Placeholder>
                    </Switch>
                    <AddPackage
                        configRef={configRef}
                        cartRef={cartRef}
                        quantityValueRef={quantityValueRef}
                        personalMessageRef={personalMessageRef}
                        packagingRef={packagingRef}
                        upsellRef={upsellRef}
                        giftWrapRef={giftWrapRef}
                    />
                    <SummaryBar
                        configRef={configRef}
                        quantityValueRef={quantityValueRef}
                        personalMessageRef={personalMessageRef}
                        packagingRef={packagingRef}
                        upsellRef={upsellRef}
                        giftWrapRef={giftWrapRef}
                    />
                </FormProvider>
            </Container>
        </ConfiguratorStyled>
    );
}

export default Configurator;
